import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import PhotoLightbox from "@/components/app/PhotoLightbox";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Camera, UploadSimple, Sparkle, Trash, ArrowsLeftRight, CaretLeft, CaretRight, Image as ImageIcon,
  Gift, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import BackButton from "@/components/app/BackButton";

const ANGLES = ["front", "side", "back"];
const ANGLE_LABEL = { front: "Front", side: "Side", back: "Back" };

const monthKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

/** Generate the last N months, newest first ("YYYY-MM"). */
function recentMonths(n = 12) {
  const out = [];
  const today = new Date();
  for (let i = 0; i < n; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    out.push(monthKey(d));
  }
  return out;
}

function PhotoTile({ photo, userId, angle, onUploadClick, onDelete, onAdjusted, busy }) {
  const [zoomed, setZoomed] = useState(false);
  const rotation = photo?.rotation || 0;
  // Bust the image cache after a rotation save so the new render reflects
  // the persisted angle immediately. We append the photo's adjusted_at /
  // uploaded_at as a cache key — server returns the raw bytes either way.
  const src = photo
    ? `${BACKEND_URL}/api/progression-photos/${photo.id}/image`
    : null;
  // For sideways thumbnails, swap aspect via the wrapper so the image still
  // fills its tile cleanly after a 90° / 270° rotation.
  const sideways = rotation % 180 !== 0;
  return (
    <div className="relative aspect-[3/4] rounded-2xl border border-line overflow-hidden bg-muted2/40 group"
      data-testid={`progress-tile-${angle}-${photo?.month || "empty"}`}>
      {src ? (
        <>
          <button
            type="button"
            onClick={() => setZoomed(true)}
            className="block w-full h-full focus:outline-none focus:ring-2 focus:ring-moss"
            aria-label={`Enlarge ${ANGLE_LABEL[angle]} photo`}
            data-testid={`enlarge-photo-${photo.id}`}
          >
            <img
              src={src}
              alt={`${ANGLE_LABEL[angle]} view`}
              loading="lazy"
              draggable={false}
              className={sideways ? "absolute inset-0 m-auto h-[133%] w-[75%] object-cover" : "w-full h-full object-cover"}
              style={rotation ? { transform: `rotate(${rotation}deg)` } : undefined}
            />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(photo); }}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-ink/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Delete photo"
            disabled={busy}
            data-testid={`delete-photo-${photo.id}`}
          >
            <Trash size={14} weight="bold" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onUploadClick(); }}
            className="absolute bottom-2 right-2 p-1.5 rounded-full bg-white/85 text-ink hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Replace photo"
            data-testid={`replace-photo-${photo.id}`}
          >
            <UploadSimple size={14} weight="bold" />
          </button>
          {zoomed && (
            <PhotoLightbox
              src={src}
              alt={`${ANGLE_LABEL[angle]} view`}
              initialRotation={rotation}
              onSave={async (adjust) => {
                const { data } = await api.patch(`/progression-photos/${photo.id}`, adjust);
                onAdjusted?.(data);
              }}
              onClose={() => setZoomed(false)}
            />
          )}
        </>
      ) : (
        <button
          type="button"
          onClick={onUploadClick}
          className="absolute inset-0 flex flex-col items-center justify-center text-ink-3 hover:text-moss hover:bg-sage/30 transition-colors"
          data-testid={`upload-photo-${angle}`}
        >
          <Camera size={28} />
          <span className="text-xs mt-2">Upload {ANGLE_LABEL[angle].toLowerCase()}</span>
        </button>
      )}
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-ink/60 text-white text-[10px] uppercase tracking-wider font-semibold">
        {ANGLE_LABEL[angle]}
      </div>
    </div>
  );
}

function UploadDialog({ open, onClose, userId, defaultMonth, defaultAngle, onUploaded }) {
  const [month, setMonth] = useState(defaultMonth);
  const [angle, setAngle] = useState(defaultAngle);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMonth(defaultMonth);
      setAngle(defaultAngle);
      setFile(null);
      setNotes("");
    }
  }, [open, defaultMonth, defaultAngle]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pick a photo first"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("user_id", userId);
    fd.append("month", month);
    fd.append("angle", angle);
    if (notes) fd.append("notes", notes);
    try {
      // Override the api instance's default JSON Content-Type — axios will then
      // auto-pick `multipart/form-data; boundary=…` from the FormData body.
      // (Manually writing 'multipart/form-data' strips the boundary and the
      // server then can't parse the request, which surfaced as a silent fail.)
      const { data } = await api.post("/progression-photos", fd, {
        headers: { "Content-Type": undefined },
      });
      toast.success(`Uploaded ${ANGLE_LABEL[angle]} – ${monthLabel(month)}`);
      onUploaded?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const months = recentMonths(13);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Camera size={18} weight="fill" className="text-moss" />
            Add progress photo
          </DialogTitle>
          <DialogDescription>
            Photos are private — only you and your coaches can see them.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="upload-photo-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="hs-label">Month</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="upload-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="hs-label">Angle</Label>
              <Select value={angle} onValueChange={setAngle}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="upload-angle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ANGLES.map((a) => (
                    <SelectItem key={a} value={a}>{ANGLE_LABEL[a]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="hs-label">Photo</Label>
            <div className="relative mt-2 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-line p-5 text-center">
              {file ? (
                <>
                  <ImageIcon size={28} className="text-moss" />
                  <div className="mt-2 text-sm font-medium truncate max-w-full">{file.name}</div>
                  <div className="text-xs text-ink-3">{Math.round(file.size / 1024)} KB</div>
                </>
              ) : (
                <>
                  <Camera size={28} className="text-ink-3" />
                  <div className="mt-2 text-sm text-ink-2">Tap to take a photo or choose from your library</div>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="absolute inset-0 opacity-0 cursor-pointer"
                style={{ position: "absolute" }}
                data-testid="upload-file-input"
              />
              <Button type="button" variant="secondary" className="mt-3 rounded-xl"
                      onClick={() => fileRef.current?.click()}>
                {file ? "Change photo" : "Upload photo"}
              </Button>
            </div>
          </div>
          <div>
            <Label className="hs-label">Notes <span className="text-ink-3 normal-case">(optional)</span></Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="mt-2 rounded-xl resize-none" rows={2}
                      placeholder="Bodyweight, how you're feeling, anything to remember…"
                      data-testid="upload-notes" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" className="rounded-xl" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !file}
                    className="rounded-xl bg-moss hover:bg-moss-hover text-white hover:text-white"
                    data-testid="upload-submit">
              {busy ? "Uploading…" : "Save photo"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CompareDialog({ open, onClose, userId, monthsWithPhotos }) {
  const sorted = useMemo(() => [...monthsWithPhotos].sort().reverse(), [monthsWithPhotos]);
  const [monthA, setMonthA] = useState("");
  const [monthB, setMonthB] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setResult(null);
      setError("");
      // Default to oldest vs newest
      if (sorted.length >= 2) {
        setMonthA(sorted[sorted.length - 1]);
        setMonthB(sorted[0]);
      } else {
        setMonthA(sorted[0] || "");
        setMonthB(sorted[0] || "");
      }
    }
  }, [open, sorted]);

  const run = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/progression-photos/compare", {
        user_id: userId,
        month_a: monthA,
        month_b: monthB,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-2xl max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-terracotta" />
            AI progress comparison
          </DialogTitle>
          <DialogDescription>
            Pick two months and we'll surface the changes Gemini sees in your photos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
            <div>
              <Label className="hs-label">Before</Label>
              <Select value={monthA} onValueChange={setMonthA}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="compare-month-a">
                  <SelectValue placeholder="Pick a month" />
                </SelectTrigger>
                <SelectContent>
                  {sorted.map((m) => (
                    <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <ArrowsLeftRight size={18} className="text-ink-3 mb-3" />
            <div>
              <Label className="hs-label">After</Label>
              <Select value={monthB} onValueChange={setMonthB}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid="compare-month-b">
                  <SelectValue placeholder="Pick a month" />
                </SelectTrigger>
                <SelectContent>
                  {sorted.map((m) => (
                    <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button onClick={run} disabled={busy || !monthA || !monthB || monthA === monthB}
                  className="w-full rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
                  data-testid="compare-run">
            <Sparkle size={16} className="mr-2" weight="fill" />
            {busy ? "Analyzing…" : "Compare with AI"}
          </Button>

          {error && (
            <div className="rounded-xl bg-error/10 border border-error/30 text-error p-3 text-sm" data-testid="compare-error">
              {error}
            </div>
          )}

          {result && (
            <div className="rounded-2xl border border-line bg-muted2/30 p-5 space-y-4" data-testid="compare-result">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="hs-label">
                  {monthLabel(result.month_a)} <span className="text-ink-3">→</span> {monthLabel(result.month_b)}
                </div>
                <div className="flex gap-1.5 text-[10px] uppercase tracking-wider">
                  {result.angles_compared.map((a) => (
                    <span key={a} className="px-2 py-0.5 rounded-full bg-sage text-moss font-semibold">{a}</span>
                  ))}
                </div>
              </div>

              <div>
                <div className="hs-label text-[10px]">Summary</div>
                <p className="mt-1 text-ink leading-snug">{result.summary}</p>
              </div>

              {Array.isArray(result.observations) && result.observations.length > 0 && (
                <div>
                  <div className="hs-label text-[10px]">Observations</div>
                  <ul className="mt-1 space-y-1.5">
                    {result.observations.map((obs, i) => (
                      <li key={i} className="flex gap-2 text-sm">
                        <span className="text-terracotta mt-1">●</span>
                        <span className="flex-1">{obs}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.coaching_note && (
                <div className="rounded-xl bg-sage/40 p-3 border-l-4 border-moss">
                  <div className="hs-label text-[10px] text-moss">Coach's note</div>
                  <p className="mt-1 text-sm text-ink italic">{result.coaching_note}</p>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-xl" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Progress() {
  const { user } = useAuth();
  const params = useParams();
  const userId = params.userId || user.id;
  const isSelf = userId === user.id;

  const [profile, setProfile] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadCfg, setUploadCfg] = useState(null);   // {month, angle} or null
  const [compareOpen, setCompareOpen] = useState(false);
  const [monthCursor, setMonthCursor] = useState(0);  // how many months back from "today"
  const [busy, setBusy] = useState(false);

  const months = recentMonths(13);
  const visibleMonths = months.slice(monthCursor, monthCursor + 3);

  const reload = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: u }] = await Promise.all([
        api.get("/progression-photos", { params: { user_id: userId } }),
        api.get(`/users/${userId}`),
      ]);
      setPhotos(p);
      setProfile(u);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);

  const monthsWithPhotos = useMemo(
    () => Array.from(new Set(photos.map((p) => p.month))).sort().reverse(),
    [photos],
  );

  const byMonthAngle = useMemo(() => {
    const map = {};
    for (const p of photos) {
      map[`${p.month}|${p.angle}`] = p;
    }
    return map;
  }, [photos]);

  const handleDelete = async (photo) => {
    if (!window.confirm(`Delete this ${photo.angle} photo from ${monthLabel(photo.month)}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/progression-photos/${photo.id}`);
      setPhotos((cur) => cur.filter((p) => p.id !== photo.id));
      toast.success("Photo deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const displayName = profile?.name || (isSelf ? "You" : "Athlete");

  return (
    <div className="p-6 sm:p-10 max-w-5xl mx-auto space-y-8">
      <BackButton />

      {user.role === "admin" && (
        <GiftsSection athleteId={userId} athleteName={displayName} />
      )}

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="hs-label">Body progression</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">
            {isSelf ? "Your progress" : `${displayName.split(" ")[0]}'s progress`}
          </h1>
          <p className="text-ink-2 mt-2 max-w-xl">
            Monthly front, side and back photos — tap to upload. Use the AI compare to see what changed.
          </p>
        </div>
        <Button
          onClick={() => setCompareOpen(true)}
          disabled={monthsWithPhotos.length < 2}
          className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white hover:text-white"
          data-testid="open-compare-dialog"
        >
          <Sparkle size={16} className="mr-2" weight="fill" /> Compare with AI
        </Button>
      </div>

      {/* Month nav */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" className="rounded-xl"
                onClick={() => setMonthCursor((c) => Math.max(0, c - 3))}
                disabled={monthCursor === 0}
                data-testid="month-prev">
          <CaretLeft size={16} className="mr-1" weight="bold" /> Newer
        </Button>
        <div className="text-sm text-ink-2">
          {monthLabel(visibleMonths[visibleMonths.length - 1])} – {monthLabel(visibleMonths[0])}
        </div>
        <Button variant="ghost" className="rounded-xl"
                onClick={() => setMonthCursor((c) => Math.min(months.length - 3, c + 3))}
                disabled={monthCursor >= months.length - 3}
                data-testid="month-next">
          Older <CaretRight size={16} className="ml-1" weight="bold" />
        </Button>
      </div>

      {/* Monthly tiles */}
      {loading ? (
        <div className="text-ink-3">Loading photos…</div>
      ) : (
        <div className="space-y-6">
          {visibleMonths.map((m) => {
            const hasAny = ANGLES.some((a) => byMonthAngle[`${m}|${a}`]);
            return (
              <Card key={m} className="hs-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="hs-label text-[10px]">Month</div>
                    <h3 className="font-heading text-xl font-semibold mt-0.5">{monthLabel(m)}</h3>
                  </div>
                  {!hasAny && (
                    <span className="px-2.5 py-1 rounded-full bg-warning/15 text-warning text-[11px] font-semibold uppercase tracking-wider">
                      Empty
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {ANGLES.map((a) => (
                    <PhotoTile
                      key={a}
                      photo={byMonthAngle[`${m}|${a}`]}
                      userId={userId}
                      angle={a}
                      busy={busy}
                      onUploadClick={() => setUploadCfg({ month: m, angle: a })}
                      onDelete={handleDelete}
                      onAdjusted={(updated) => setPhotos((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))}
                    />
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <UploadDialog
        open={!!uploadCfg}
        onClose={() => setUploadCfg(null)}
        userId={userId}
        defaultMonth={uploadCfg?.month || months[0]}
        defaultAngle={uploadCfg?.angle || "front"}
        onUploaded={() => reload()}
      />

      <CompareDialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        userId={userId}
        monthsWithPhotos={monthsWithPhotos}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Gifts section — admin-only visibility. Lists everything HST has given
// the athlete and the date, with inline add + per-row delete.
// ────────────────────────────────────────────────────────────────

function GiftsSection({ athleteId, athleteName }) {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    item: "",
    date: new Date().toISOString().slice(0, 10),
    note: "",
  });

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${athleteId}/gifts`);
      setGifts(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [athleteId]);

  const addGift = async (e) => {
    e?.preventDefault?.();
    if (!form.item.trim() || !form.date) {
      toast.error("Item and date are required");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post(`/users/${athleteId}/gifts`, {
        item: form.item.trim(),
        date: form.date,
        note: form.note.trim() || null,
      });
      setGifts((cur) => [data, ...cur]);
      setForm({ item: "", date: new Date().toISOString().slice(0, 10), note: "" });
      toast.success("Gift logged");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const removeGift = async (g) => {
    if (!window.confirm(`Remove "${g.item}" from the gift log?`)) return;
    try {
      await api.delete(`/users/${athleteId}/gifts/${g.id}`);
      setGifts((cur) => cur.filter((x) => x.id !== g.id));
      toast.success("Gift removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const fmtDate = (d) => {
    try {
      const [y, m, day] = d.split("-").map(Number);
      return new Date(y, m - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return d;
    }
  };

  return (
    <Card className="hs-card p-5 sm:p-6" data-testid="gifts-section">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl grid place-items-center bg-terracotta/20 text-terracotta">
          <Gift size={18} weight="bold" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold">HST Gift Log</h2>
          <p className="text-xs text-ink-3 mt-0.5">
            Head-coach only · Track everything HealthSpan Training has given {athleteName}.
          </p>
        </div>
      </div>

      <form onSubmit={addGift} className="mt-5 grid grid-cols-1 sm:grid-cols-[1fr_160px_auto] gap-3 items-end" data-testid="gift-add-form">
        <div>
          <Label className="hs-label">Item</Label>
          <input
            type="text"
            value={form.item}
            onChange={(e) => setForm({ ...form, item: e.target.value })}
            placeholder="e.g. HST hoodie, lifting straps, gift card"
            className="mt-1 h-11 w-full rounded-xl border border-line bg-bone px-3 font-data text-sm focus:outline-none focus:ring-2 focus:ring-moss/30"
            data-testid="gift-input-item"
          />
        </div>
        <div>
          <Label className="hs-label">Date given</Label>
          <input
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            className="mt-1 h-11 w-full rounded-xl border border-line bg-bone px-3 font-data text-sm focus:outline-none focus:ring-2 focus:ring-moss/30"
            data-testid="gift-input-date"
          />
        </div>
        <Button
          type="submit"
          disabled={adding}
          className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white h-11"
          data-testid="gift-add-button"
        >
          <Plus size={16} weight="bold" className="mr-1" />
          {adding ? "Adding…" : "Add gift"}
        </Button>
        <div className="sm:col-span-3">
          <Label className="hs-label">Note (optional)</Label>
          <input
            type="text"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            placeholder="e.g. referral thank-you, birthday, 1-year anniversary"
            className="mt-1 h-11 w-full rounded-xl border border-line bg-bone px-3 font-data text-sm focus:outline-none focus:ring-2 focus:ring-moss/30"
            data-testid="gift-input-note"
          />
        </div>
      </form>

      <div className="mt-6">
        {loading ? (
          <p className="text-sm text-ink-3">Loading…</p>
        ) : gifts.length === 0 ? (
          <p className="text-sm text-ink-3" data-testid="gifts-empty">
            No gifts logged yet. Add the first one above.
          </p>
        ) : (
          <ul className="divide-y divide-line" data-testid="gifts-list">
            {gifts.map((g) => (
              <li
                key={g.id}
                className="py-3 flex items-start justify-between gap-3"
                data-testid={`gift-row-${g.id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm">{g.item}</div>
                  <div className="text-[11px] text-ink-3 font-data uppercase tracking-[0.14em] mt-0.5">
                    {fmtDate(g.date)}
                  </div>
                  {g.note && (
                    <div className="text-xs text-ink-2 mt-1 italic">{g.note}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeGift(g)}
                  className="shrink-0 h-9 w-9 rounded-xl grid place-items-center text-ink-3 hover:text-terracotta hover:bg-terracotta/10 transition-colors"
                  aria-label={`Remove ${g.item}`}
                  data-testid={`gift-delete-${g.id}`}
                >
                  <Trash size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
